import {
  formatHumanName,
  getDisplayString,
  getExtensionValue,
  getReferenceString,
  getTypedPropertyValue,
  isEmpty,
  MedplumClient,
  OperationOutcomeError,
  parseReference,
  PatchOperation,
  ValueSetExpandParams,
  WithId,
} from "@medplum/core";
import {
  Bundle,
  BundleEntry,
  CodeableConcept,
  Coding,
  ContactPoint,
  Device,
  Extension,
  ExtensionValue,
  ExtractResource,
  HumanName,
  Identifier,
  List,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  Provenance,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  Reference,
  RelatedPerson,
  Resource,
  ResourceType,
  ValueSetExpansionContains,
} from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";
import isArray from "lodash/isArray";
import { v7 as uuidv7 } from "uuid";

import { ORGANIZATION_ID } from "@/constants/local-storage";
import { Extensions } from "@/fhir/enums/extensions";
import { ProvenanceParticipantType } from "@/fhir/enums/fhir-valuesets";
import { Systems } from "@/fhir/enums/systems";
import { isPatchTestFailedError } from "@/utils/medplum-patch-test-failed";
import { getCanonicalHumanName } from "@/utils/person";

export type ExtensibleResource = Resource & {
  extension?: Array<{
    url: string;
    value?: unknown;
  }>;
};

export function joinReferenceString(resourceType: string, referenceId: string): string {
  return `${resourceType}/${referenceId}`;
}

/**
 * Resource id from a transaction-response `location`. Accepts relative (`Type/id`), versioned
 * (`Type/id/_history/vid`), and absolute FHIR URLs. Returns undefined when the location is
 * missing, malformed, or names a different resource type.
 */
export function resourceIdFromLocation(
  location: string | undefined,
  expectedType: string,
): string | undefined {
  if (!location) return undefined;

  try {
    const [resourceType, resourceId] = parseReference({
      reference: referenceFromLocation(location),
    });
    return resourceType === expectedType && resourceId ? resourceId : undefined;
  } catch {
    return undefined;
  }
}

function referenceFromLocation(location: string): string {
  const path = location.split(/[?#]/, 1)[0] ?? location;
  const historyIndex = path.indexOf("/_history/");
  const withoutHistory = historyIndex === -1 ? path : path.slice(0, historyIndex);
  const segments = withoutHistory.split("/").filter(Boolean);
  if (segments.length < 2) return location;
  return joinReferenceString(segments[segments.length - 2]!, segments[segments.length - 1]!);
}

/**
 * Every `Identifier.value` a resource carries under one system, in array order. Entries with no
 * value are skipped, so the result is always a list of real values.
 */
export function identifierValues(
  resource: { identifier?: Identifier[] },
  system: string,
): string[] {
  return (resource.identifier ?? [])
    .filter((identifier) => identifier.system === system)
    .map((identifier) => identifier.value)
    .filter((value): value is string => Boolean(value));
}

/**
 * Returns the first `QuestionnaireResponse` logical id found on `Observation.derivedFrom`, using
 * Medplum `parseReference` (no ad-hoc `split`). Malformed entries and non-QuestionnaireResponse
 * references are skipped in array order.
 *
 * @param derivedFrom `Observation.derivedFrom` (or equivalent reference list)
 * @returns QuestionnaireResponse id, or `undefined` if none qualify
 */
export function getQuestionnaireResponseIdFromObservationDerivedFrom(
  derivedFrom: Reference[] | undefined,
): string | undefined {
  for (const derived of derivedFrom ?? []) {
    const referenceString = derived.reference;
    if (!referenceString) continue;
    try {
      const [resourceType, resourceId] = parseReference({ reference: referenceString });
      if (resourceType === "QuestionnaireResponse" && resourceId) {
        return resourceId;
      }
    } catch {
      continue;
    }
  }
  return undefined;
}

export function getCategory(categories: CodeableConcept[] | undefined, system: string) {
  if (!categories) return;
  return categories.find((category) => category.coding?.[0]?.system === system)?.coding?.[0];
}

export function getCodeableConceptCoding(
  codeableConcept: CodeableConcept | undefined,
  index: number = 0,
): Coding | undefined {
  return codeableConcept?.coding?.[index];
}

export function getCodeableConceptDisplay(
  codeableConcept: CodeableConcept | undefined,
  index: number = 0,
  fallback: string = "",
): string {
  return getCodingDisplayLabel(getCodeableConceptCoding(codeableConcept, index), fallback);
}

/**
 * Returns a human-readable label from a FHIR Coding, ignoring non-string `display` values.
 * Falls back to `code` when display is missing or malformed.
 */
export function getCodingDisplayLabel(coding: Coding | undefined, fallback: string = ""): string {
  if (!coding) return fallback;

  const { display } = coding;
  if (typeof display === "string" && display.trim().length > 0) {
    return display.trim();
  }

  if (typeof display === "object" && display !== null) {
    const nestedDisplay = (display as Coding).display;
    if (typeof nestedDisplay === "string" && nestedDisplay.trim().length > 0) {
      return nestedDisplay.trim();
    }
  }

  if (typeof coding.code === "string" && coding.code.trim().length > 0) {
    return coding.code.trim();
  }

  return fallback;
}

/**
 * Joins all Coding labels on a CodeableConcept for multi-select answers (e.g. FHR relation to contractions).
 *
 * Returns `undefined` when no non-empty labels are present. This matches optional-field semantics;
 * callers that skip empty strings also skip `undefined`.
 */
export function getCodeableConceptCodingLabels(
  codeableConcept: CodeableConcept | undefined,
  separator = ", ",
): string | undefined {
  const labels =
    codeableConcept?.coding
      ?.map((coding) => getCodingDisplayLabel(coding))
      .filter((label) => label.length > 0) ?? [];

  return labels.length > 0 ? labels.join(separator) : undefined;
}

export function getCodeableConceptCode(
  codeableConcept: CodeableConcept | undefined,
  index: number = 0,
): string {
  return getCodeableConceptCoding(codeableConcept, index)?.code || "";
}

export function getCodeableConceptSystem(
  codeableConcept: CodeableConcept | undefined,
  index: number = 0,
): string {
  return getCodeableConceptCoding(codeableConcept, index)?.system || "";
}

export interface FindCodingInCodeableConceptCriteria {
  code?: string;
  system?: string;
}

/**
 * Returns the first Coding in a CodeableConcept whose `code` and/or `system`
 * match the given criteria. Omitted criteria fields are not compared.
 * When both `code` and `system` are omitted, returns `undefined`.
 *
 * @param codeableConcept - FHIR CodeableConcept (may be undefined).
 * @param criteria - Match on `code` and/or `system` when each is provided.
 * @returns The first matching Coding, or undefined if none match.
 */
export function findCodingInCodeableConcept(
  codeableConcept: CodeableConcept | undefined,
  criteria: FindCodingInCodeableConceptCriteria,
): Coding | undefined {
  if (criteria.code === undefined && criteria.system === undefined) {
    return undefined;
  }
  const codings = codeableConcept?.coding ?? [];
  return codings.find((coding) => {
    if (criteria.code !== undefined && coding.code !== criteria.code) {
      return false;
    }
    if (criteria.system !== undefined && coding.system !== criteria.system) {
      return false;
    }
    return true;
  });
}

export function getAnswer(linkId: string, value: unknown, type: string): QuestionnaireResponseItem {
  const baseAnswer = { linkId };
  if (isEmpty(value)) return baseAnswer;

  return { ...baseAnswer, answer: [{ [type]: value }] };
}

export function setExtensionValue<T>(
  extensions: Extension[] | undefined,
  url: string,
  value: Record<string, T>,
) {
  return [
    ...(extensions?.filter((extension) => extension.url !== url) ?? []),
    {
      url,
      ...value,
    },
  ];
}

export function removeExtensions(extensions: Extension[] | undefined, urls: string[]) {
  return extensions?.filter((extension) => !urls.includes(extension.url)) ?? [];
}

export function getResourcesFromBundle<T extends Resource>(
  resourceType: string,
  bundle: Bundle<T> | undefined,
): T[] {
  const entries = bundle?.entry ?? [];

  return entries
    .filter((entry) => entry.resource?.resourceType === resourceType)
    .map((entry) => entry.resource!);
}

export function getReferencesFromList(resourceType: string, list: List, flag?: Coding) {
  const entries = list?.entry ?? [];

  return entries
    .filter((entry) => {
      const [entryResourceType] = parseReference(entry.item);
      if (entryResourceType !== resourceType) return false;

      if (flag) {
        return entry.flag?.coding?.some(
          (coding) => coding.system === flag.system && coding.code === flag.code,
        );
      }

      return true;
    })
    .map((entry) => entry.item!);
}

/**
 * Retrieves the values of specified extensions from a resource.
 * Returns an array of extension values for the given URLs, or undefined if none are found.
 *
 * @param resource - The resource to search for extensions
 * @param urls - The URLs of the extensions to search for
 *
 * @returns An array of extension values if found, otherwise undefined.
 */
export function getExtensionValues<T extends ExtensibleResource>(
  resource: T,
  ...urls: string[]
): ExtensionValue[] | undefined {
  let extensions: Extension[] = [];

  for (const url of urls) {
    extensions = [...extensions, ...(resource.extension?.filter((e) => e.url === url) ?? [])];
  }

  if (!extensions?.length) {
    return undefined;
  }
  const typedValues = extensions
    .map((extension) => getTypedPropertyValue({ type: "Extension", value: extension }, "value[x]"))
    .filter(Boolean);
  if (!typedValues?.length) {
    return undefined;
  }

  return typedValues.map((extension) =>
    Array.isArray(extension) ? extension![0].value : extension!.value,
  );
}

/**
 * Filters and limits ValueSet expansion contains based on params
 */
function filterAndLimitContains(
  contains: ValueSetExpansionContains[] | undefined,
  params?: ValueSetExpandParams,
): ValueSetExpansionContains[] {
  let results = contains ?? [];

  if (params?.filter) {
    const filterLower = params.filter.toLowerCase();
    results = results.filter((item) => {
      const codeMatch = item.code?.toLowerCase().includes(filterLower);
      const displayMatch = item.display?.toLowerCase().includes(filterLower);
      return codeMatch || displayMatch;
    });
  }

  if (params?.count && results.length > params.count) {
    results = results.slice(0, params.count);
  }

  return results;
}

export async function getValueSetOptions(
  medplum: MedplumClient,
  url: string | undefined,
  params?: ValueSetExpandParams,
): Promise<ValueSetExpansionContains[]> {
  if (!url) return [];

  // Try direct fetch first when filtering (for pre-expanded ValueSets)
  if (params?.filter) {
    try {
      const valueSet = await medplum.searchOne("ValueSet", { url });
      if (valueSet?.expansion?.contains) {
        return filterAndLimitContains(valueSet.expansion.contains, params);
      }
    } catch (error) {
      console.warn("Failed to fetch ValueSet directly, using valueSetExpand:", error);
    }
  }

  // Fall back to valueSetExpand (either no filter, or direct fetch failed)
  const valueSet = await medplum.valueSetExpand({ url, ...(params ?? {}) });
  return filterAndLimitContains(valueSet?.expansion?.contains, params);
}

export function findQuestionnaireItem(
  questionnaireItems: QuestionnaireItem[] | undefined,
  linkId: string,
): QuestionnaireItem | undefined {
  for (const item of questionnaireItems ?? []) {
    if (item.type === "group") {
      const nestedItem = findQuestionnaireItem(item.item ?? [], linkId);
      if (nestedItem) return nestedItem;
    }

    if (item.linkId === linkId) {
      return item;
    }
  }
}

export function getQuestionnaireResponseItems(
  linkId: string,
  questionnaireResponseItems: QuestionnaireResponseItem[] | undefined,
  multiple: boolean = false,
): QuestionnaireResponseItem[] {
  const result: QuestionnaireResponseItem[] = [];

  for (const item of questionnaireResponseItems ?? []) {
    if (item.linkId === linkId) {
      result.push(item);
      if (!multiple) return result;
    }
    if (item.item) {
      result.push(...getQuestionnaireResponseItems(linkId, item.item, multiple));
    }
  }

  return result;
}

/**
 * Reads an item's integer answer, counting it only when marked accepted.
 *
 * @param items The questionnaire response's items to search, at any nesting depth.
 * @param linkId The item's linkId to look up.
 * @returns The accepted integer answer, or undefined if the item is missing, unaccepted, or has no integer answer.
 */
export function getAcceptedIntegerAnswer(
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string,
): number | undefined {
  const [item] = getQuestionnaireResponseItems(linkId, items);

  if (!item) return undefined;

  const isAccepted = getExtensionValue(item, Extensions.QuestionnaireResponseItemIsAccepted);
  if (isAccepted !== true) return undefined;

  return item.answer?.[0]?.valueInteger;
}

export interface NestedExtensionConfig {
  parentUrl: string;
  nestedFields: {
    url: string;
    value: string | number | boolean | Coding;
  }[];
  filterField: {
    url: string;
    compareValue: string;
  };
}

/**
 * Merges existing extensions with new ones, filtering based on a specific field.
 * This method is useful for updating extensions while maintaining those belonging to other contexts.
 *
 * @param prevExtensions - Array of existing extensions that may be undefined
 * @param currentExtensions - New extensions to be added
 * @param filterField - Object containing URL and comparison value for filtering extensions
 * @param filterField.url - URL of the field used for filtering
 * @param filterField.compareValue - Value used for comparison in the filter
 *
 * @returns Combined array of extensions, excluding those matching the filter criteria
 *
 * @example
 * const result = mergeNestedExtensions(
 *   previousExtensions,
 *   newExtensions,
 *   { url: 'calendar-id', compareValue: '123' }
 * );
 */
export const mergeNestedExtensions = (
  prevExtensions: Extension[] | undefined,
  currentExtensions: Extension[],
  filterField: { url: string; compareValue: string },
): Extension[] => {
  const otherExtensions =
    prevExtensions?.filter((extension) => {
      const fieldValue = extension.extension?.find(
        (nested) => nested.url === filterField.url,
      )?.valueString;
      return fieldValue !== filterField.compareValue;
    }) ?? [];

  return [...otherExtensions, ...currentExtensions];
};

/**
 * Creates an array of nested extensions from an array of values and a configuration.
 *
 * @template T - Type of values in the input array
 * @param values - Array of values to be transformed into extensions
 * @param config - Configuration for creating extensions
 * @param config.parentUrl - Parent extension URL
 * @param config.nestedFields - Array of nested fields
 * @param config.nestedFields[].url - Nested field URL
 * @param config.nestedFields[].value - Field value (use empty string for dynamic array value)
 *
 * @returns Array of formatted FHIR extensions
 *
 * @example
 * const config = {
 *   parentUrl: "appointment-type",
 *   nestedFields: [
 *     { url: "calendar-id", value: "123" },
 *     { url: "type-id", value: "" }
 *   ],
 *   filterField: {
 *     url: "calendar-id",
 *     compareValue: "123"
 *   }
 * };
 *
 * const extensions = createNestedExtensions(["type1", "type2"], config);
 */
export const createNestedExtensions = <T>(
  values: T[],
  config: NestedExtensionConfig,
): Extension[] => {
  return values.map((value) => ({
    url: config.parentUrl,
    extension: config.nestedFields.map((field) => ({
      url: field.url,
      [`value${typeof field.value === "string" ? "String" : typeof field.value === "number" ? "Integer" : "Boolean"}`]:
        field.value === "" ? value : field.value,
    })),
  }));
};

/**
 * Retrieves the value of a nested extension from an array of extensions.
 * Uses Medplum's getTypedPropertyValue to handle all possible value types.
 *
 * @param extensions - Array of extensions
 * @param url - URL of the nested extension
 * @returns Value of the nested extension or undefined if not found
 */
export const getNestedExtensionValue = (extensions: Extension | undefined, url: string) => {
  if (!extensions || !url || !extensions.extension) return;

  const extension = extensions?.extension?.find((extension) => extension.url === url);

  if (!extension) return;

  const typedValue = getTypedPropertyValue({ type: "Extension", value: extension }, "value[x]");
  return isArray(typedValue) ? typedValue[0].value : typedValue?.value;
};

export const getPractitionerName = async (medplum: MedplumClient, practitionerId: string) => {
  const practitioner = await medplum.readResource("Practitioner", practitionerId);
  const firstName = practitioner?.name?.[0]?.given?.[0] ?? "";
  const lastName = practitioner?.name?.[0]?.family ?? "";
  const fullName = `${firstName} ${lastName}`.trim();
  const initials = `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();

  return [fullName, lastName, initials, fullName];
};

export type SimplifiedValue = string | number | boolean | null;

type SimplifiedQuestionnaireResponseAnswer = {
  display: string;
  value: SimplifiedValue[];
};

type SimplifiedQuestionnaireResponse = Record<string, SimplifiedQuestionnaireResponseAnswer>;

/**
 * Simplifies a QuestionnaireResponse object by extracting the display and value of each answer.
 *
 * @param questionnaireResponse - The QuestionnaireResponse object to simplify
 * @returns An object with linkId as keys and display and value as values
 */
export const getQuestionnaireResponseAnswersSimplified = (
  questionnaireResponse?: QuestionnaireResponse,
): SimplifiedQuestionnaireResponse => {
  const result: SimplifiedQuestionnaireResponse = {};

  if (!questionnaireResponse || !questionnaireResponse.item) {
    return {};
  }

  for (const item of questionnaireResponse.item) {
    if (!item.answer || item.answer.length === 0) {
      continue;
    }

    const values: SimplifiedValue[] = [];

    for (const answer of item.answer) {
      let value: SimplifiedValue = null;

      switch (true) {
        case answer.valueString !== undefined:
          value = answer.valueString;
          break;
        case answer.valueInteger !== undefined:
          value = answer.valueInteger;
          break;
        case answer.valueDecimal !== undefined:
          value = answer.valueDecimal;
          break;
        case answer.valueBoolean !== undefined:
          value = answer.valueBoolean;
          break;
        case answer.valueDate !== undefined:
          value = answer.valueDate;
          break;
        case answer.valueTime !== undefined:
          value = answer.valueTime;
          break;
        case answer.valueDateTime !== undefined:
          value = answer.valueDateTime;
          break;
        case answer.valueCoding !== undefined:
          value = answer.valueCoding.display || answer.valueCoding.code || "";
          break;
        default:
          value = null;
      }

      values.push(value);
    }

    const key = item.linkId;
    const display = item.text || item.linkId;

    if (!result[key]) {
      result[key] = {
        display,
        value: values,
      };
    } else {
      result[key].value = [...result[key].value, ...values];
    }
  }

  return result;
};

export const setNextRedirectUrl = () => {
  // /logout isn't a page to return to, so send both providers and patients straight to the
  // sign-in page rather than encoding it into a ?next= redirect.
  if (location.pathname.includes("/logout")) {
    window.location.href = "/";
    return;
  }
  // Already at the sign-in page — don't re-encode the current URL into another
  // /?next= layer, which would create an infinitely-nested redirect loop.
  if (location.pathname === "/") {
    return;
  }
  const next = encodeURIComponent(location.pathname + location.search);
  window.location.href = `/?next=${next}`;
};

export const isExpiredSession = (medplum: MedplumClient): boolean => {
  const isOnRoot = location.pathname === "/";
  const isAuthenticated = !!medplum.getActiveLogin();
  const hasProfile = !!medplum.getProfile();

  return !isOnRoot && (!isAuthenticated || !hasProfile);
};

export const booleanToYesOrNo = (value: boolean | undefined | null): string => {
  if (value === undefined || value === null) return "";

  return value ? "Yes" : "No";
};

export const getActiveOrganizationId = () => localStorage.getItem(ORGANIZATION_ID)!;

export const getActiveOrganizationReference = () =>
  joinReferenceString("Organization", getActiveOrganizationId());

export const getOrganizationReference = (organizationId: string | undefined) =>
  organizationId
    ? joinReferenceString("Organization", organizationId)
    : getActiveOrganizationReference();

export const isPractitionerReference = (
  actor: Reference | undefined,
): actor is Reference<Practitioner> => {
  return (
    typeof actor === "object" &&
    actor !== null &&
    "reference" in actor &&
    typeof actor.reference === "string" &&
    actor.reference.startsWith("Practitioner/")
  );
};

export const formatHumanOfficialName = (names: HumanName[] | undefined) => {
  if (!names) return "";

  const name = getCanonicalHumanName(names);
  return name ? formatHumanName(name) : "";
};

/**
 * Checks if the current user is a ClientApplication and should be restricted from accessing the main application
 * @param profile - The user profile from useMedplumProfile()
 * @returns true if the user is a ClientApplication that should be restricted
 */
export function isRestrictedClientApplication(
  profile: { resourceType?: string } | undefined,
): boolean {
  return profile?.resourceType === "ClientApplication";
}

/**
 * Checks if the current pathname is allowed for ClientApplication users
 * @param pathname - The current pathname
 * @returns true if the route is allowed for ClientApplication users
 */
export function isClientApplicationAllowedRoute(pathname: string): boolean {
  const isRoot = pathname === "/";
  const isScheduleClient = pathname.includes("/schedule/client/");
  const isScheduleClass = pathname.includes("/schedule/class/");
  const isRegisterClient = pathname.includes("/register/") && pathname.includes("/clients/");
  const isSetPasswordClient = pathname.includes("/set-password/") && pathname.includes("/clients/");
  const isResetPasswordClient = pathname.includes("/reset-password");
  return (
    isRoot ||
    isScheduleClient ||
    isScheduleClass ||
    isRegisterClient ||
    isSetPasswordClient ||
    isResetPasswordClient
  );
}

/**
 * Routes where unauthenticated users sign in or complete OAuth. Used to avoid blocking the UI
 * on Medplum loading state (e.g. after token expiry) while the client restores persisted logins.
 */
export function isPublicAuthPathname(pathname: string): boolean {
  return pathname === "/" || pathname === "/oauth";
}

/**
 * Handles ClientApplication access restriction by signing out and redirecting
 * @param medplum - The Medplum client instance
 */
export function handleClientApplicationRestriction(medplum: MedplumClient): void {
  medplum.signOut();
  setNextRedirectUrl();
}

export async function getDisplayStringAsync<T extends Resource>(
  medplum: MedplumClient,
  reference: Reference<T> | undefined,
  fallback: string = "",
) {
  if (!reference) return fallback;

  if (reference.display) return reference.display;

  const resource = (await medplum.readReference(reference)) as T;
  return getDisplayString(resource);
}

export function joinTokenString(
  system: string | undefined,
  code: string | undefined,
): string | undefined {
  if (!code) return undefined;
  if (!system) return code;
  return `${system}|${code}`;
}

/** Escapes a value for a `_filter` string literal. Backslashes go first, so a backslash this adds
 * is not escaped again. */
export function escapeSearchValue(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export function hasExtensionValue(
  resource: { extension?: Extension[] } | undefined,
  url: string,
  value: string | number | boolean,
): boolean {
  if (!resource?.extension) return false;

  return resource.extension.some((ext) => {
    if (ext.url !== url) return false;

    if (ext.valueBoolean === value) return true;
    if (ext.valueString === value) return true;
    if (ext.valueInteger === value) return true;
    if (ext.valueDecimal === value) return true;
    if (ext.valueUri === value) return true;
    if (ext.valueCode === value) return true;

    if (
      typeof value === "string" &&
      ext.valueCodeableConcept?.coding?.some((c) => c.code === value)
    ) {
      return true;
    }

    return false;
  });
}

export async function patchWithRetries<T extends ResourceType>(
  medplum: MedplumClient,
  resourceType: T,
  resourceId: string,
  operations: PatchOperation[],
  { retries = 0, maxRetries = 2 }: { retries?: number; maxRetries?: number } = {},
): Promise<WithId<ExtractResource<T>>> {
  try {
    return await medplum.patchResource<T>(resourceType, resourceId, operations);
  } catch (error) {
    if (!(error instanceof OperationOutcomeError) || operations?.[0]?.op !== "test") {
      throw error;
    }

    if (isPatchTestFailedError(error)) {
      if (retries < maxRetries) {
        const resource = await medplum.searchOne<T>(resourceType, {
          _id: resourceId,
          _elements: "id",
        });
        const versionId = resource?.meta?.versionId;
        if (!versionId) throw error;

        const updatedOperations: PatchOperation[] = [
          {
            op: "test",
            path: "/meta/versionId",
            value: versionId,
          },
          ...operations.slice(1),
        ];

        return await patchWithRetries<T>(medplum, resourceType, resourceId, updatedOperations, {
          retries: retries + 1,
          maxRetries,
        });
      }
    }

    throw error;
  }
}

const MISSING_CONDITIONAL_CREATE_RESULT_ERROR =
  "Conditional create returned no resource for the entry it was sent";

export function wasEntryCreated(entry: BundleEntry | undefined): boolean {
  return Number.parseInt(entry?.response?.status ?? "", 10) === 201;
}

/**
 * A conditional create answers 201 when it wrote and 200 when it matched, but
 * `createResourceIfNoneExist` returns the parsed body alone — the client reads that status only to
 * branch on it, and never hands it back. A one-entry batch carries it in the payload instead, so
 * the caller is told which of the two happened rather than inferring it from the resource.
 */
export async function getOrCreateResource<T extends Resource>(
  medplum: MedplumClient,
  resource: T,
  query: string,
): Promise<[WithId<T>, boolean]> {
  const bundle = await medplum.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry: [
      {
        resource,
        request: { method: "POST", url: resource.resourceType, ifNoneExist: query },
      },
    ],
  });

  const entry = bundle.entry?.[0];

  if (!entry?.resource) {
    throw new Error(MISSING_CONDITIONAL_CREATE_RESULT_ERROR);
  }

  return [entry.resource as WithId<T>, wasEntryCreated(entry)];
}

export function getPracticeIdForPatient(patient: Patient | undefined): string | undefined {
  if (!patient) {
    Sentry.captureException(new Error("Patient is undefined"));
    return undefined;
  }

  let practiceId =
    patient.managingOrganization?.reference != null && patient.managingOrganization.reference !== ""
      ? parseReference(patient.managingOrganization)?.[1]
      : undefined;
  if (!practiceId) {
    const orgCompartment = patient.meta?.compartment?.find(
      (compartment) =>
        compartment?.reference != null &&
        compartment.reference !== "" &&
        parseReference(compartment)[0] === "Organization",
    );
    practiceId =
      orgCompartment?.reference != null && orgCompartment.reference !== ""
        ? parseReference(orgCompartment)?.[1]
        : undefined;
  }

  if (!practiceId) {
    Sentry.captureException(new Error("Practice ID is undefined"));
    return undefined;
  }

  return practiceId;
}

/**
 * Returns the value of the first contact point that matches the given system and has a value.
 *
 * @param telecom - Contact points to search
 * @param system - The contact point system to look for
 * @returns The matching value, or `undefined` when none is present
 */
export function getContactPointValue(
  telecom: ContactPoint[] | undefined,
  system: ContactPoint["system"],
): string | undefined {
  return telecom?.find((contactPoint) => contactPoint.system === system && contactPoint.value)
    ?.value;
}

export function generateFullUrl(uuid?: string): string {
  return `urn:uuid:${uuid ?? uuidv7()}`;
}

export async function fetchAuthorReferenceFromProvenance<T extends Resource>(
  medplum: MedplumClient,
  resource: T,
  provenance?: Provenance,
): Promise<
  | Reference<Device | Organization | Patient | Practitioner | PractitionerRole | RelatedPerson>
  | undefined
> {
  if (!provenance) {
    provenance = await medplum.searchOne("Provenance", {
      target: getReferenceString(resource),
      _sort: "-_lastUpdated",
    });
  }

  const authorAgent = provenance?.agent?.find((agent) => {
    const coding = agent.type?.coding?.[0];
    return (
      coding?.code === ProvenanceParticipantType.Author &&
      coding?.system === Systems.HL7ProvenanceParticipantType
    );
  });

  return authorAgent?.who;
}
